'use client'

import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'

export type DragPosition = { x: number; y: number }
export type DragDirection = 'ltr' | 'rtl'

export type UseDraggableStorage = 'session' | 'local' | false

export type UseDraggableOptions = {
    /** Viewport edge padding while dragging and after resize. Default: 8 */
    margin?: number
    /**
     * Distance from the viewport bottom when pinning the element after height changes.
     * Should match the element's CSS `bottom` value. Falls back to `margin`.
     */
    bottomInset?: number
    /**
     * When true, keeps the bottom edge at `bottomInset` as the element grows (e.g. chat messages).
     * Without this, top-anchored drag positions let the panel extend below the viewport.
     */
    pinBottomOnResize?: boolean
    /** Persist position across visits. Default: false */
    storage?: UseDraggableStorage
    /** sessionStorage / localStorage key when storage is enabled */
    storageKey?: string
    /**
     * Skip drag when pointerdown originates from interactive descendants
     * (button, input, link, etc.). Useful for header handles that also contain controls.
     * Set false when the handle is a dedicated drag icon/button only.
     * Default: true
     */
    excludeInteractiveTargets?: boolean
    /** Disable dragging without unmounting the element */
    enabled?: boolean
    /** Layout direction for persisted drag positions. Defaults to the document direction. */
    direction?: DragDirection
}

export type DragHandleProps = {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
}

export type UseDraggableReturn<T extends HTMLElement = HTMLElement> = {
    /** Attach to the element that should move */
    elementRef: RefObject<T | null>
    /** Spread onto one or more drag-handle elements (header, icon, grip, etc.) */
    dragHandleProps: DragHandleProps
    /** Clears imperative position and restores caller's default layout styles */
    resetPosition: () => void
    /** Current position without subscribing to React state (null = default layout) */
    getPosition: () => DragPosition | null
}

const DEFAULT_MARGIN = 8
const INTERACTIVE_DRAG_TARGET_SELECTOR =
    'button, input, textarea, a, [role="switch"], label'

const isDragDirection = (value: unknown): value is DragDirection =>
    value === 'ltr' || value === 'rtl'

const getDocumentDirection = (): DragDirection => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return 'ltr'
    if (document.documentElement.dir === 'rtl' || document.body.dir === 'rtl') return 'rtl'
    return window.getComputedStyle(document.documentElement).direction === 'rtl' ? 'rtl' : 'ltr'
}

const isInteractiveDragTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(target.closest(INTERACTIVE_DRAG_TARGET_SELECTOR))
}

const clampPosition = (
    x: number,
    y: number,
    width: number,
    height: number,
    margin: number
): DragPosition => ({
    x: Math.min(Math.max(x, margin), window.innerWidth - width - margin),
    y: Math.min(Math.max(y, margin), window.innerHeight - height - margin)
})

const readStoredPosition = (
    storage: UseDraggableStorage,
    storageKey: string | undefined,
    direction: DragDirection
): DragPosition | null => {
    if (storage === false || !storageKey || typeof window === 'undefined') return null
    const store = storage === 'session' ? sessionStorage : localStorage
    try {
        const raw = store.getItem(storageKey)
        if (!raw) return null
        const parsed = JSON.parse(raw) as Partial<DragPosition> & { direction?: unknown }
        if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null
        if (isDragDirection(parsed.direction) && parsed.direction !== direction) return null
        if (!isDragDirection(parsed.direction) && direction === 'rtl') return null
        return { x: parsed.x, y: parsed.y }
    } catch {
        return null
    }
}

const writeStoredPosition = (
    storage: UseDraggableStorage,
    storageKey: string | undefined,
    position: DragPosition,
    direction: DragDirection
) => {
    if (storage === false || !storageKey || typeof window === 'undefined') return
    const store = storage === 'session' ? sessionStorage : localStorage
    store.setItem(storageKey, JSON.stringify({ ...position, direction }))
}

const clearStoredPosition = (storage: UseDraggableStorage, storageKey: string | undefined) => {
    if (storage === false || !storageKey || typeof window === 'undefined') return
    const store = storage === 'session' ? sessionStorage : localStorage
    store.removeItem(storageKey)
}

const applyPositionToElement = (el: HTMLElement, position: DragPosition) => {
    el.style.top = `${position.y}px`
    el.style.left = `${position.x}px`
    el.style.right = 'auto'
    el.style.bottom = 'auto'
    el.style.transform = 'none'
}

const clearPositionFromElement = (el: HTMLElement) => {
    el.style.top = ''
    el.style.left = ''
    el.style.right = ''
    el.style.bottom = ''
    el.style.transform = ''
}

const setDraggingUi = (el: HTMLElement | null, dragging: boolean) => {
    if (!el) return
    if (dragging) {
        el.dataset.dragging = 'true'
        document.body.style.cursor = 'grabbing'
        document.body.style.userSelect = 'none'
    } else {
        delete el.dataset.dragging
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
    }
}

export const useDraggable = <T extends HTMLElement = HTMLDivElement>(
    options: UseDraggableOptions = {}
): UseDraggableReturn<T> => {
    const {
        margin = DEFAULT_MARGIN,
        bottomInset,
        pinBottomOnResize = false,
        storage = false,
        storageKey,
        excludeInteractiveTargets = true,
        enabled = true,
        direction
    } = options

    const viewportBottomInset = bottomInset ?? margin
    const resolvedDirection = direction ?? getDocumentDirection()

    const elementRef = useRef<T | null>(null)
    const positionRef = useRef<DragPosition | null>(readStoredPosition(storage, storageKey, resolvedDirection))
    const hasCustomPositionRef = useRef(positionRef.current !== null)
    const prevHeightRef = useRef<number | null>(null)

    const dragStateRef = useRef<{
        pointerId: number
        startX: number
        startY: number
        originX: number
        originY: number
    } | null>(null)

    const applyPosition = useCallback(
        (next: DragPosition) => {
            const el = elementRef.current
            if (!el) return next
            const clamped = clampPosition(
                next.x,
                next.y,
                el.getBoundingClientRect().width,
                el.getBoundingClientRect().height,
                margin
            )
            positionRef.current = clamped
            hasCustomPositionRef.current = true
            applyPositionToElement(el, clamped)
            return clamped
        },
        [margin]
    )

    const resolvePosition = useCallback((): DragPosition => {
        if (positionRef.current) return positionRef.current
        const el = elementRef.current
        if (!el) return { x: margin, y: margin }
        const rect = el.getBoundingClientRect()
        return { x: rect.left, y: rect.top }
    }, [margin])

    const pinBottomEdge = useCallback(() => {
        const el = elementRef.current
        if (!el || dragStateRef.current) return

        const { height, bottom: currentBottom, left } = el.getBoundingClientRect()
        const targetBottom = window.innerHeight - viewportBottomInset

        if (hasCustomPositionRef.current && positionRef.current) {
            const anchoredBottom =
                prevHeightRef.current != null
                    ? positionRef.current.y + prevHeightRef.current
                    : currentBottom
            const nextY = Math.min(anchoredBottom, targetBottom) - height
            applyPosition({ x: positionRef.current.x, y: nextY })
        } else if (currentBottom > targetBottom + 1) {
            applyPosition({ x: left, y: targetBottom - height })
        }

        prevHeightRef.current = height
    }, [applyPosition, viewportBottomInset])

    const handleDragPointerMove = useCallback(
        (event: PointerEvent) => {
            const dragState = dragStateRef.current
            const el = elementRef.current
            if (!dragState || !el || event.pointerId !== dragState.pointerId) return

            applyPosition({
                x: dragState.originX + (event.clientX - dragState.startX),
                y: dragState.originY + (event.clientY - dragState.startY)
            })
        },
        [applyPosition]
    )

    const handleDragPointerUp = useCallback(
        (event: PointerEvent) => {
            const dragState = dragStateRef.current
            if (!dragState || event.pointerId !== dragState.pointerId) return

            dragStateRef.current = null
            setDraggingUi(elementRef.current, false)
            elementRef.current?.releasePointerCapture(event.pointerId)
            window.removeEventListener('pointermove', handleDragPointerMove)
            window.removeEventListener('pointerup', handleDragPointerUp)
            window.removeEventListener('pointercancel', handleDragPointerUp)

            if (positionRef.current) {
                writeStoredPosition(storage, storageKey, positionRef.current, resolvedDirection)
            }
        },
        [handleDragPointerMove, resolvedDirection, storage, storageKey]
    )

    useEffect(
        () => () => {
            window.removeEventListener('pointermove', handleDragPointerMove)
            window.removeEventListener('pointerup', handleDragPointerUp)
            window.removeEventListener('pointercancel', handleDragPointerUp)
            setDraggingUi(elementRef.current, false)
        },
        [handleDragPointerMove, handleDragPointerUp]
    )

    useEffect(() => {
        const el = elementRef.current
        const storedPosition = readStoredPosition(storage, storageKey, resolvedDirection)
        positionRef.current = storedPosition
        hasCustomPositionRef.current = storedPosition !== null
        prevHeightRef.current = null
        if (!el) return
        if (!storedPosition) {
            clearPositionFromElement(el)
            return
        }
        applyPosition(storedPosition)
        if (pinBottomOnResize) {
            pinBottomEdge()
        }
    }, [applyPosition, pinBottomEdge, pinBottomOnResize, resolvedDirection, storage, storageKey])

    useEffect(() => {
        const handleResize = () => {
            if (!hasCustomPositionRef.current || !positionRef.current) return
            if (pinBottomOnResize) {
                pinBottomEdge()
                return
            }
            applyPosition(positionRef.current)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [applyPosition, pinBottomEdge, pinBottomOnResize])

    useEffect(() => {
        if (!pinBottomOnResize) return

        const el = elementRef.current
        if (!el) return

        const observer = new ResizeObserver(() => {
            pinBottomEdge()
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [pinBottomEdge, pinBottomOnResize])

    const handlePointerDown = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (!enabled || event.button !== 0) return
            if (excludeInteractiveTargets && isInteractiveDragTarget(event.target)) return

            const origin = resolvePosition()
            dragStateRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                originX: origin.x,
                originY: origin.y
            }
            applyPosition(origin)
            setDraggingUi(elementRef.current, true)
            elementRef.current?.setPointerCapture(event.pointerId)
            window.addEventListener('pointermove', handleDragPointerMove)
            window.addEventListener('pointerup', handleDragPointerUp)
            window.addEventListener('pointercancel', handleDragPointerUp)
        },
        [
            enabled,
            excludeInteractiveTargets,
            resolvePosition,
            applyPosition,
            handleDragPointerMove,
            handleDragPointerUp
        ]
    )

    const resetPosition = useCallback(() => {
        dragStateRef.current = null
        positionRef.current = null
        hasCustomPositionRef.current = false
        prevHeightRef.current = null
        const el = elementRef.current
        if (el) clearPositionFromElement(el)
        setDraggingUi(el, false)
        clearStoredPosition(storage, storageKey)
    }, [storage, storageKey])

    const getPosition = useCallback(() => positionRef.current, [])

    return {
        elementRef,
        dragHandleProps: { onPointerDown: handlePointerDown },
        resetPosition,
        getPosition
    }
}
